import { SpeechBubbleStyle } from '../utils/speechStyles';

export type Area = { x_min: number; y_min: number; x_max: number; y_max: number };

/** Возвращает базовый WS URL.
 *  - REACT_APP_WS_URL=ws://host/path  → используется как есть
 *  - REACT_APP_WS_URL=/path           → резолвится от текущего origin
 *  - не задан                         → /ws от текущего origin
 */
export function getWsBase(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const env = process.env.REACT_APP_WS_URL;
  if (!env) return `${proto}//${window.location.host}/ws-app`;
  if (env.startsWith('ws://') || env.startsWith('wss://')) return env;
  console.log("ws-base",env)
  return `${proto}//${window.location.host}${env}`;
}

export type WsMessage =
  | { type: 'move'; direction: string }
  | { type: 'subscribe'; area: Area }
  | { type: 'say'; text: string; style?: SpeechBubbleStyle }
  | { type: 'jump' };

export type WsEvent =
  | { type: 'init'; points: any[] }
  | { type: 'points_update'; points: any[] }
  | { type: 'moved'; pointId: number; newX: number; newY: number; serverMs?: number }
  | { type: 'error'; message: string }
  | { type: 'eviction_warning'; zone_name: string; seconds_left: number }
  | { type: 'evicted'; zone_name: string; new_x: number; new_y: number }
  | { type: 'said'; point_id: number; x: number; y: number; text: string; style?: SpeechBubbleStyle }
  | { type: 'jumped'; point_id: number }
  | { type: 'point_removed'; point_id: number };

export type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export interface WsStatusInfo {
  status: WsStatus;
  lastMessageAt: number | null;
  messageCount: number;
  reconnectAttempt: number;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private bearerToken?: string;
  private onEvent: (event: WsEvent) => void;
  private onStatusChange?: (info: WsStatusInfo) => void;
  private reconnectAttempts = 0;
  private maxReconnect = 5;
  private closed = false;
  private currentStatus: WsStatus = 'connecting';
  private lastMessageAt: number | null = null;
  private messageCount = 0;

  constructor(
    url: string,
    onEvent: (event: WsEvent) => void,
    onStatusChange?: (info: WsStatusInfo) => void,
    bearerToken?: string,
  ) {
    this.url = url;
    this.bearerToken = bearerToken;
    this.onEvent = onEvent;
    this.onStatusChange = onStatusChange;
    this.emitStatus('connecting');
    this.connect();
  }

  private emitStatus(status: WsStatus) {
    this.currentStatus = status;
    this.onStatusChange?.({
      status,
      lastMessageAt: this.lastMessageAt,
      messageCount: this.messageCount,
      reconnectAttempt: this.reconnectAttempts,
    });
  }

  private connect() {
    // Pass JWT via the Sec-WebSocket-Protocol header (`bearer.<jwt>`) instead
    // of the URL query string. Browsers do not let you set arbitrary headers
    // on a WebSocket handshake, but they DO send Sec-WebSocket-Protocol from
    // the second constructor argument — so we abuse it as a side-channel that
    // does not leak into access logs / Referer / browser history.
    if (this.bearerToken) {
      this.ws = new WebSocket(this.url, [`bearer.${this.bearerToken}`]);
    } else {
      this.ws = new WebSocket(this.url);
    }
    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.emitStatus('connected');
    };
    this.ws.onmessage = (e) => {
      this.lastMessageAt = Date.now();
      this.messageCount++;
      try {
        const data = JSON.parse(e.data);
        this.onEvent(data);
      } catch (err) {
        console.error('Invalid WS message', err);
      }
      // emit on every message so tooltip stats stay current
      this.onStatusChange?.({
        status: this.currentStatus,
        lastMessageAt: this.lastMessageAt,
        messageCount: this.messageCount,
        reconnectAttempt: this.reconnectAttempts,
      });
    };
    this.ws.onerror = () => {
      console.error('WebSocket error');
    };
    this.ws.onclose = () => {
      if (this.closed) return;
      if (this.reconnectAttempts < this.maxReconnect) {
        this.emitStatus('reconnecting');
        setTimeout(() => {
          if (this.closed) return;
          this.reconnectAttempts++;
          this.connect();
        }, 1000 * Math.pow(2, this.reconnectAttempts));
      } else {
        this.emitStatus('disconnected');
      }
    };
  }

  send(message: WsMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  close() {
    this.closed = true;
    this.ws?.close();
  }
}
